import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BootstrapControllerService } from 'projects/api-swagger-client/src';
import { environment } from 'src/environments/environment';
import { Logger } from './logger.service';
import { deobfuscate } from './bootstrap-obfuscation';

const log = new Logger('RuntimeConfig');

/**
 * Fase 3 de la migración a Infisical — configuración que el backend entrega al
 * arrancar, en vez de venir horneada en el bundle.
 *
 * Se carga una sola vez desde `GET /bootstrap` mediante un `APP_INITIALIZER`,
 * antes de que exista una sesión, y queda en memoria. Mientras se valida el
 * camino nuevo, los valores fijos de `environment` siguen como respaldo: si el
 * endpoint no responde, la app arranca igual con lo de siempre.
 */
@Injectable({
  providedIn: 'root',
})
export class RuntimeConfigService {
  private values = {
    clientId: environment.client_id,
    firebase: environment.firebase as Record<string, string>,
  };

  constructor(private bootstrapControllerService: BootstrapControllerService) {}

  /** `client_id` de OAuth2 que se manda al hacer login. */
  get clientId(): string {
    return this.values.clientId;
  }

  /** Configuración del SDK de Firebase (apiKey, projectId, vapidKey, ...). */
  get firebase(): Record<string, string> {
    return this.values.firebase;
  }

  /**
   * Consulta el backend y reemplaza los valores de respaldo. Nunca rechaza: un
   * fallo aquí bloquearía el arranque de toda la app, y lo que se pierde es la
   * configuración remota, no la capacidad de operar.
   */
  async load(): Promise<void> {
    try {
      const response = await firstValueFrom(this.bootstrapControllerService.bootstrapControllerBootstrap());
      const payload = (response?.data as { payload?: string })?.payload;
      if (!payload) {
        log.warn('El backend no devolvió payload, se usan los valores locales');
        return;
      }
      // Viene ofuscado (no cifrado, ver bootstrap-obfuscation.ts).
      const remoto = deobfuscate<{ clientId?: string; firebase?: Record<string, string> }>(payload);
      if (!remoto.clientId) {
        log.warn('El backend no devolvió clientId, se usa el valor local');
        return;
      }
      // La config de Firebase se fusiona en vez de reemplazarse: si el backend
      // todavía no tiene alguna llave cargada, se conserva la local en vez de
      // dejar el campo vacío.
      const firebase = { ...this.values.firebase, ...(remoto.firebase ?? {}) };
      this.values = { ...this.values, clientId: remoto.clientId, firebase };
    } catch (error) {
      log.warn('No se pudo leer la configuración de arranque, se usan los valores locales', error);
    }
  }
}

/** Factory para el `APP_INITIALIZER` de `AppModule`. */
export function loadRuntimeConfig(runtimeConfig: RuntimeConfigService): () => Promise<void> {
  return () => runtimeConfig.load();
}
